import { Box, Flex, HStack, Icon, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { motion, useInView, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { IconType } from "react-icons";
import { FiBell, FiCreditCard, FiEyeOff, FiFileText, FiSend, FiToggleRight, FiUploadCloud } from "react-icons/fi";
import { MdPix } from "react-icons/md";
import { EASE_OUT } from "./SectionHeading";

const MotionBox = motion(Box);

// Fictional brand skins: the point is one app, re-themed by configuration.
const SKINS = ["#e8a44a", "#34d399", "#5b8def", "#f4658b", "#9575ef", "#3cc8d8", "#a8d94a", "#f2764b"];
const GLYPH_RADIUS = ["50%", "9px", "4px 14px 4px 14px"];
const TOTAL_BRANDS = 60;
const SWAP_MS = 1900;
const COLOR_TRANSITION = "background-color 0.7s ease, color 0.7s ease, border-color 0.7s ease, box-shadow 0.7s ease";

const quickActions: { label: string; icon: IconType }[] = [
  { label: "Pix", icon: MdPix },
  { label: "Pagar", icon: FiFileText },
  { label: "Enviar", icon: FiSend },
  { label: "Cartões", icon: FiCreditCard },
];

function PhoneFrame({ children, brand }: { children: ReactNode; brand: string }) {
  return (
    <Box
      position="relative"
      w="full"
      borderRadius="44px"
      p="9px"
      bg="#08080b"
      borderWidth="1px"
      borderColor="whiteAlpha.300"
      boxShadow={`0 40px 90px -20px ${brand}55, 0 30px 60px rgba(0,0,0,0.55)`}
      transition={COLOR_TRANSITION}
      style={{ aspectRatio: "9 / 18.6" }}
    >
      <Box position="relative" w="full" h="full" borderRadius="36px" overflow="hidden" bg="#111116">
        {children}
      </Box>
    </Box>
  );
}

function GhostPhone({ brand, side }: { brand: string; side: "left" | "right" }) {
  const sign = side === "left" ? -1 : 1;
  return (
    <Box
      position="absolute"
      inset={0}
      display={{ base: "none", md: "block" }}
      opacity={0.38}
      filter="blur(1.5px)"
      transform={`translateX(${sign * 46}%) translateY(4%) rotate(${sign * 7}deg) scale(0.84)`}
      aria-hidden
    >
      <PhoneFrame brand={brand}>
        <Box p={5} pt={12}>
          <Box h="96px" borderRadius="18px" bg={brand} transition={COLOR_TRANSITION} />
          <VStack align="stretch" spacing={3} mt={6}>
            {[70, 52, 64, 40].map((w) => (
              <Box key={w} h="10px" w={`${w}%`} borderRadius="full" bg="whiteAlpha.200" />
            ))}
          </VStack>
        </Box>
      </PhoneFrame>
    </Box>
  );
}

function FloatingChip({
  icon,
  children,
  delay,
  ...position
}: {
  icon: IconType;
  children: ReactNode;
  delay: number;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
}) {
  return (
    <MotionBox
      position="absolute"
      zIndex={3}
      display={{ base: "none", md: "block" }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay, ease: EASE_OUT }}
      {...position}
    >
      <MotionBox
        animate={{ y: [0, -7, 0] }}
        transition={{ duration: 5 + delay, repeat: Infinity, ease: "easeInOut" }}
      >
        <HStack
          spacing={2}
          px={3}
          py={2}
          borderRadius="full"
          bg="rgba(24,24,30,0.86)"
          backdropFilter="blur(12px)"
          borderWidth="1px"
          borderColor="whiteAlpha.300"
          boxShadow="0 12px 30px rgba(0,0,0,0.45)"
          fontFamily="mono"
          fontSize="11px"
          color="graphite.100"
          whiteSpace="nowrap"
        >
          <Icon as={icon} color="var(--brand)" boxSize="14px" transition={COLOR_TRANSITION} />
          <Text as="span">{children}</Text>
        </HStack>
      </MotionBox>
    </MotionBox>
  );
}

export function PhoneShowcase() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { margin: "-10% 0px" });
  const prefersReducedMotion = useReducedMotion();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!isInView || prefersReducedMotion) return;
    const id = window.setInterval(() => setStep((s) => s + 1), SWAP_MS);
    return () => window.clearInterval(id);
  }, [isInView, prefersReducedMotion]);

  const rotateX = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useMotionValue(0), { stiffness: 120, damping: 18 });

  useEffect(() => {
    if (prefersReducedMotion) return;
    function handlePointerMove(event: PointerEvent) {
      if (event.pointerType !== "mouse") return;
      const px = event.clientX / window.innerWidth - 0.5;
      const py = event.clientY / window.innerHeight - 0.5;
      rotateY.set(px * 14);
      rotateX.set(py * -10);
    }
    window.addEventListener("pointermove", handlePointerMove);
    return () => window.removeEventListener("pointermove", handlePointerMove);
  }, [prefersReducedMotion, rotateX, rotateY]);

  const brand = SKINS[step % SKINS.length];
  const brandNumber = String((step % TOTAL_BRANDS) + 1).padStart(2, "0");

  return (
    <Box ref={ref} w="full" style={{ ["--brand" as string]: brand }}>
      <MotionBox
        position="relative"
        w={{ base: "236px", md: "272px", xl: "292px" }}
        mx="auto"
        initial={{ opacity: 0, y: 70, rotate: 4 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 1, delay: 0.35, ease: EASE_OUT }}
        aria-hidden
      >
        <GhostPhone brand={SKINS[(step + 3) % SKINS.length]} side="left" />
        <GhostPhone brand={SKINS[(step + 5) % SKINS.length]} side="right" />

        <FloatingChip icon={FiUploadCloud} delay={1.1} top="13%" left="-34%">
          build → lojas
        </FloatingChip>
        <FloatingChip icon={FiToggleRight} delay={1.3} bottom="20%" right="-36%">
          feature flag
        </FloatingChip>

        <MotionBox position="relative" zIndex={2} style={{ rotateX, rotateY, transformPerspective: 1100 }}>
          <PhoneFrame brand={brand}>
            <Box
              position="absolute"
              top="10px"
              left="50%"
              transform="translateX(-50%)"
              w="74px"
              h="22px"
              borderRadius="full"
              bg="#08080b"
              zIndex={2}
            />

            <Flex direction="column" h="full" px={4} pt="46px" pb={3}>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Box
                    w="26px"
                    h="26px"
                    bg="var(--brand)"
                    borderRadius={GLYPH_RADIUS[step % GLYPH_RADIUS.length]}
                    transition={`${COLOR_TRANSITION}, border-radius 0.7s ease`}
                  />
                  <Box overflow="hidden" h="18px">
                    <MotionBox
                      key={brandNumber}
                      initial={prefersReducedMotion ? false : { y: 14, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.4, ease: EASE_OUT }}
                    >
                      <Text fontFamily="mono" fontSize="11px" lineHeight="18px" color="graphite.100">
                        marca {brandNumber}
                      </Text>
                    </MotionBox>
                  </Box>
                </HStack>
                <Icon as={FiBell} color="graphite.400" boxSize="15px" />
              </HStack>

              <Text mt={5} fontSize="10px" color="graphite.400">
                Saldo disponível
              </Text>
              <HStack spacing={2} mt={0.5}>
                <Text fontFamily="heading" fontWeight="700" fontSize="22px" color="white" letterSpacing="0.02em">
                  R$ ••••••
                </Text>
                <Icon as={FiEyeOff} color="graphite.500" boxSize="13px" />
              </HStack>

              <Box
                mt={4}
                h="27%"
                borderRadius="18px"
                bg="var(--brand)"
                position="relative"
                overflow="hidden"
                transition={COLOR_TRANSITION}
              >
                <Box position="absolute" inset={0} bgGradient="linear(135deg, whiteAlpha.400, transparent 45%, blackAlpha.500)" />
                <Box position="absolute" top="14px" left="14px" w="30px" h="22px" borderRadius="5px" bg="blackAlpha.400" />
                <Text position="absolute" bottom="12px" left="14px" fontFamily="mono" fontSize="11px" color="blackAlpha.800">
                  •••• •••• ••••
                </Text>
                {!prefersReducedMotion ? (
                  <MotionBox
                    key={step}
                    position="absolute"
                    top={0}
                    bottom={0}
                    w="45%"
                    bgGradient="linear(100deg, transparent, whiteAlpha.600, transparent)"
                    initial={{ x: "-140%" }}
                    animate={{ x: "340%" }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                  />
                ) : null}
              </Box>

              <SimpleGrid columns={4} gap={2} mt={4}>
                {quickActions.map((action) => (
                  <VStack key={action.label} spacing={1}>
                    <Flex
                      w="38px"
                      h="38px"
                      borderRadius="13px"
                      bg="whiteAlpha.100"
                      align="center"
                      justify="center"
                      color="var(--brand)"
                      transition={COLOR_TRANSITION}
                    >
                      <Icon as={action.icon} boxSize="16px" />
                    </Flex>
                    <Text fontSize="9px" color="graphite.300">
                      {action.label}
                    </Text>
                  </VStack>
                ))}
              </SimpleGrid>

              <VStack align="stretch" spacing={3} mt={5} flex={1} overflow="hidden">
                {[62, 44, 54].map((w) => (
                  <HStack key={w} spacing={3}>
                    <Box w="26px" h="26px" borderRadius="full" bg="whiteAlpha.100" flexShrink={0} />
                    <Box flex={1}>
                      <Box h="7px" w={`${w}%`} borderRadius="full" bg="whiteAlpha.300" />
                      <Box h="6px" w={`${w - 22}%`} borderRadius="full" bg="whiteAlpha.100" mt="6px" />
                    </Box>
                    <Box h="7px" w="30px" borderRadius="full" bg="whiteAlpha.200" />
                  </HStack>
                ))}
              </VStack>

              <HStack justify="space-around" pt={3} borderTopWidth="1px" borderColor="whiteAlpha.100">
                {[0, 1, 2, 3].map((i) => (
                  <Box
                    key={i}
                    w={i === 0 ? "18px" : "6px"}
                    h="6px"
                    borderRadius="full"
                    bg={i === 0 ? "var(--brand)" : "whiteAlpha.300"}
                    transition={COLOR_TRANSITION}
                  />
                ))}
              </HStack>
            </Flex>
          </PhoneFrame>
        </MotionBox>
      </MotionBox>

      <Text
        mt={7}
        textAlign="center"
        fontFamily="mono"
        fontSize="11px"
        letterSpacing="0.08em"
        color="graphite.400"
      >
        mesma base, marca {brandNumber} de {TOTAL_BRANDS}+
        <Box as="span" display="block" mt={1} color="graphite.400" opacity={0.8} letterSpacing="0.04em">
          demonstração ilustrativa
        </Box>
      </Text>
    </Box>
  );
}
